import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ModernLogo } from "@/components/ModernLogo";
import { PaymentCalculator } from "@/components/PaymentCalculator";
import { 
  UserPlus,
  MessageSquare,
  Search,
  CheckCircle,
  CreditCard,
  Star,
  ArrowRight,
  ArrowLeft,
  Clock,
  Shield,
  Award,
  Phone,
  FileCheck,
  Users,
  Zap
} from "lucide-react";
import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";

export function HowItWorksCustomer() {
  const steps = [
    {
      step: 1,
      title: "Post Your Job",
      description: "Describe your project in under 5 minutes",
      icon: <FileCheck className="w-8 h-8 text-primary" />,
      details: [
        "Choose from our trade categories",
        "Add photos and detailed description",
        "Set your preferred timeline and budget",
        "Specify your location and urgency level"
      ],
      timeRequired: "5 minutes",
      difficulty: "Easy"
    },
    {
      step: 2,
      title: "Create Your Account",
      description: "Quick registration to connect with tradesmen",
      icon: <UserPlus className="w-8 h-8 text-primary" />,
      details: [
        "Simple email and phone verification",
        "Add your contact preferences",
        "Set up secure payment method",
        "Choose notification settings"
      ],
      timeRequired: "3 minutes",
      difficulty: "Easy"
    },
    {
      step: 3,
      title: "Receive Quotes",
      description: "Get fast responses from verified tradesmen",
      icon: <MessageSquare className="w-8 h-8 text-primary" />,
      details: [
        "Verified tradesmen respond with detailed quotes",
        "View tradesman profiles, ratings, and photos",
        "Compare prices and read customer reviews",
        "Ask questions through secure messaging"
      ],
      timeRequired: "Fast",
      difficulty: "Easy"
    },
    {
      step: 4,
      title: "Choose Your Tradesman",
      description: "Select the best tradesman for your needs",
      icon: <Search className="w-8 h-8 text-primary" />,
      details: [
        "Compare quotes side by side",
        "Check insurance and certification badges",
        "Read verified customer reviews and ratings",
        "Choose based on price, timeline, and expertise"
      ],
      timeRequired: "30 minutes",
      difficulty: "Medium"
    },
    {
      step: 5,
      title: "Secure Payment",
      description: "Pay 3% booking fee + 7% deposit to platform upfront",
      icon: <CreditCard className="w-8 h-8 text-primary" />,
      details: [
        "Pay 3% booking fee + 7% deposit upfront to platform",
        "Pay remaining 93% directly to tradesman upon completion",
        "Total you pay: quote + 3% booking fee (e.g., £1030 for £1000)",
        "Full money-back guarantee if unsatisfied"
      ],
      timeRequired: "2 minutes",
      difficulty: "Easy"
    },
    {
      step: 6,
      title: "Job Completion & Review",
      description: "Enjoy professional service and leave feedback",
      icon: <Star className="w-8 h-8 text-primary" />,
      details: [
        "Track progress through photos and updates",
        "Confirm completion when satisfied",
        "Rate your tradesman's service",
        "Build a history for future bookings"
      ],
      timeRequired: "As scheduled",
      difficulty: "Easy"
    }
  ];

  const benefits = [
    {
      icon: <Shield className="w-6 h-6 text-primary" />,
      title: "Verified Tradesmen Only",
      description: "All tradesmen are fully insured, certified, and verified before joining our platform."
    },
    {
      icon: <Clock className="w-6 h-6 text-primary" />,
      title: "Fast Response Guarantee",
      description: "Get fast responses from qualified tradesmen, or we'll help find alternatives."
    },
    {
      icon: <CreditCard className="w-6 h-6 text-primary" />,
      title: "Secure Payment Protection",
      description: "Your money is held safely until you're completely satisfied with the completed work."
    },
    {
      icon: <Users className="w-6 h-6 text-primary" />,
      title: "Expert Customer Support",
      description: "Our UK-based support team is available to help resolve any issues throughout your project."
    },
    {
      icon: <Award className="w-6 h-6 text-primary" />,
      title: "Quality Guarantee",
      description: "If you're not satisfied with the work, we'll make it right or provide a full refund."
    },
    {
      icon: <MessageSquare className="w-6 h-6 text-primary" />,
      title: "Direct Communication",
      description: "Chat directly with your chosen tradesman through our secure messaging platform."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <SEO
        title="How It Works for Customers - Find Trusted Tradespeople | Sortable"
        description="Learn how to find reliable tradespeople on Sortable. Post your job, get quotes, and connect with verified local contractors. Simple 4-step process for homeowners."
        keywords="find a tradesperson, find a local tradesperson, Sortable tradesperson finder, trusted plumber, vetted electrician, local builder near me, Sortable reviews, compare tradespeople, how to choose a builder, emergency plumber, find a gardener, Sortable alternative, rated and reviewed tradespeople, insured plumber, find a tradesperson near me, Sortable login, book a tradesperson, tradesperson quotes, reliable electrician, Checkatrade alternative, MyBuilder alternative, trusted tradespeople near me, Sortable app, how to find a good builder, what to ask a tradesperson, tradesperson quote template, home improvement tips, emergency plumber, boiler repair, leaking tap, kitchen installation cost, bathroom fitting, rewiring house, patio laying ideas, fence installation, damp proofing solutions, roof repair cost, signs you need an electrician, average cost of a new kitchen, how much does a new bathroom cost, emergency locksmith, gutter cleaning, plastering services, landscaper near me"
        canonical="https://sortable.lovable.app/how-it-works-customer"
        ogImage="https://sortable.lovable.app/hero-tradesman.jpg"
      />
      {/* Header */}
      <header className="bg-background/95 backdrop-blur-sm border-b border-border/50 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-2 md:py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <ModernLogo />
            </Link>
            <div className="flex items-center gap-1 md:gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="h-3 w-3 md:h-4 md:w-4 md:mr-2" />
                  <span className="hidden md:inline">Back to Home</span>
                  <span className="md:hidden sr-only">Back</span>
                </Button>
              </Link>
              <Link to="/">
                <Button variant="default" size="sm">
                  <span className="md:hidden">Start</span>
                  <span className="hidden md:inline">Get Started</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 px-4">
        <div className="container mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-gradient-hero/10 border border-primary/20 text-primary px-6 py-3 rounded-full text-lg font-semibold mb-8 shadow-elegant">
            <Users className="h-5 w-5" />
            How It Works for Customers
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-hero bg-clip-text text-transparent">
            From Problem to Solution in 6 Simple Steps
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Get your property maintenance sorted quickly and safely. Our verified tradesmen 
            respond quickly with competitive quotes and guaranteed quality.
          </p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-12 px-4">
        <div className="container mx-auto">
          <div className="space-y-16">
            {steps.map((step, index) => (
              <div key={index} className={`flex flex-col lg:flex-row gap-8 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                <div className="flex-1">
                  <Card className="border-border/50 shadow-elegant hover:shadow-glow transition-all duration-300">
                    <CardHeader>
                      <div className="flex items-center gap-4 mb-4">
                        <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center">
                          {step.icon}
                        </div>
                        <div>
                          <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mb-2">
                            {step.step}
                          </div>
                          <CardTitle className="text-2xl">{step.title}</CardTitle>
                        </div>
                      </div>
                      <CardDescription className="text-lg">{step.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3 mb-6">
                        {step.details.map((detail, detailIndex) => (
                          <div key={detailIndex} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                            <span className="text-muted-foreground">{detail}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-4 text-sm">
                        <div className="flex items-center gap-2 text-primary">
                          <Clock className="w-4 h-4" />
                          <span>{step.timeRequired}</span>
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Shield className="w-4 h-4" />
                          <span>{step.difficulty}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                <div className="flex-shrink-0">
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block">
                      <ArrowRight className="w-8 h-8 text-primary/50" />
                    </div>
                  )}
                  {index < steps.length - 1 && (
                    <div className="lg:hidden">
                      <div className="w-px h-8 bg-primary/50 mx-auto"></div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 px-4 bg-background/50">
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-muted-foreground">
              Pay once, get quality service with complete protection.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Card className="border-border/50 shadow-elegant">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">What You Pay</CardTitle>
                <CardDescription className="text-lg">
                  All-inclusive pricing with no hidden fees
                </CardDescription>
              </CardHeader>
               <CardContent>
                <div className="text-center space-y-6">
                  <div className="bg-primary/5 p-8 rounded-lg border border-primary/20">
                    <div className="text-xl font-bold text-primary mb-2">3% Booking Fee</div>
                    <div className="text-base text-muted-foreground mb-2">Added on top • Paid to platform upfront</div>
                    <div className="text-3xl font-bold mb-2">+</div>
                    <div className="text-xl font-bold text-primary mb-2">7% Deposit</div>
                    <div className="text-base text-muted-foreground mb-2">Of the quote • Paid to platform upfront</div>
                    <div className="text-3xl font-bold mb-2">+</div>
                    <div className="text-2xl font-bold mb-2">93% of Quote</div>
                    <div className="text-base text-muted-foreground">Paid directly to tradesman at completion</div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                    <div className="text-center p-4">
                      <Shield className="w-8 h-8 text-green-600 mx-auto mb-2" />
                      <div className="font-semibold">Payment Protection</div>
                      <div className="text-sm text-muted-foreground">Money held until satisfied</div>
                    </div>
                    <div className="text-center p-4">
                      <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                      <div className="font-semibold">Quality Guarantee</div>
                      <div className="text-sm text-muted-foreground">Full refund if unsatisfied</div>
                    </div>
                    <div className="text-center p-4">
                      <Users className="w-8 h-8 text-green-600 mx-auto mb-2" />
                      <div className="font-semibold">Expert Support</div>
                      <div className="text-sm text-muted-foreground">Help throughout your project</div>
                    </div>
                  </div>
                  
                   <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                     <p className="text-sm text-muted-foreground text-center mb-4">
                      <strong>Example:</strong> For a £500 quote: Pay £50 upfront to platform (£15 booking + £35 deposit)<br />
                      Then pay £465 directly to tradesman at completion. Total you pay: £515
                     </p>
                   </div>

                   <div className="mt-6">
                     <PaymentCalculator userType="customer" />
                   </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 bg-background/50">
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose Our Platform?
            </h2>
            <p className="text-lg text-muted-foreground">
              We've built the safest and most reliable way to find trusted tradesmen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-border/50 text-center hover:shadow-elegant transition-all duration-300">
                <CardHeader>
                  <div className="bg-primary/10 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
                    {benefit.icon}
                  </div>
                  <CardTitle className="text-lg">{benefit.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {benefit.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            {[
              {
                question: "How quickly will I get responses to my job?",
                answer: "Our verified tradesmen respond quickly during business hours. For emergency jobs, responses are often immediate."
              },
              {
                question: "Are all tradesmen on the platform insured and verified?", 
                answer: "Yes, every tradesman undergoes comprehensive verification including insurance checks, certification validation, and background screening before joining."
              },
              {
                question: "What if I'm not satisfied with the work?",
                answer: "We offer a comprehensive quality guarantee. If you're not satisfied, we'll work with the tradesman to resolve issues or provide a full refund."
              },
              {
                question: "How does the payment system work?",
                answer: "Payment is held securely until you confirm the job is complete to your satisfaction. This protects both you and the tradesman."
              },
              {
                question: "Can I communicate directly with tradesmen?",
                answer: "Yes, our platform includes secure messaging so you can discuss job details, ask questions, and receive updates directly."
              },
              {
                question: "What types of jobs can I post?",
                answer: "We cover all trades including plumbing, electrical, carpentry, heating, decorating, roofing, and general maintenance work."
              }
            ].map((faq, index) => (
              <Card key={index} className="border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg">{faq.question}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 px-4 bg-gradient-hero">
        <div className="container mx-auto text-center text-primary-foreground">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Join thousands of satisfied customers who've found reliable tradesmen through our platform.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/">
              <Button size="lg" variant="secondary" className="bg-white text-primary hover:bg-white/90">
                Post Your First Job Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="border-white text-primary bg-white hover:bg-white/90" asChild>
              <a href="tel:08000438880">
                <Phone className="mr-2 h-5 w-5" />
                Need Help? Call Us
              </a>
            </Button>
          </div>
          <p className="text-sm mt-4 opacity-75">
            Post a job in 5 minutes • Get fast responses • 100% satisfaction guaranteed
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-background border-t border-border/50 py-12 px-4">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <ModernLogo showTagline={false} />
              <p className="text-muted-foreground">
                The UK's most trusted platform for finding verified, professional tradesmen.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Customers</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/how-it-works-customer" className="hover:text-primary">How It Works</Link></li>
                <li><Link to="/about" className="hover:text-primary">About Us</Link></li>
                <li><Link to="/success-stories" className="hover:text-primary">Success Stories</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Tradesmen</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/how-it-works" className="hover:text-primary">How It Works</Link></li>
                <li><Link to="/for-tradesmen" className="hover:text-primary">Join as Tradesman</Link></li>
                <li><Link to="/pricing" className="hover:text-primary">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <div className="space-y-2 text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>08000438880</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  <span>realise@post.com</span>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-border/50 mt-8 pt-8 text-center text-muted-foreground">
            <p>&copy; 2025 Sortable. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}